import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import Router from "./Router";
import { Helmet } from "react-helmet";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import Loader from "./components/Loader";
import Header from "./components/Header";
import Cookies from "universal-cookie";
import Footer from "./components/Footer";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";
import ToastMessage from "./components/ToastMessage";
import { USER_API } from "./common/common";
import {
  applySiteFavicon,
  getImageUrl,
  isMobileHeaderView,
  Logout,
  markIdleLogoutHome,
  SESSION_EXPIRED_EVENT,
} from "./common/Funcation";
import { getApi } from "./service";
import { getTheme } from "./common/StyleSeter";
import { useIdleLogout } from "./hooks/useIdleLogout";
import ZoomablePageContent, {
  isAccountPath,
  useIsMobileView,
} from "./components/ZoomablePageContent";
import {
  normalizeMobileHeaderId,
  readHeaderCache,
  writeHeaderCache,
} from "./common/mobileHeader";
import {
  subscribeTabResume,
  syncMobileFooterViewportClass,
} from "./common/mobileFooter";

const Header2 = lazy(() => import("./components/Header2"));
// import dollar from "../public/images/dollar.svg"
// import dollarRed from "../public/images/dollar-red.svg"
const HeaderWrapper = () => {
  const location = useLocation();
  const cookies = new Cookies();
  const authToken = cookies.get("skyTokenFront");
  // Select isAuthenticated from Redux to force re-render when auth state changes
  const isAuthenticated = useSelector((e: any) => e.isAuthenticated);
  const DD = useSelector((e: any) => e.domainDetails);
  const cached = useMemo(() => readHeaderCache(), []);
  const hasSite = !!(DD && Object.keys(DD).length);
  const headerId = normalizeMobileHeaderId(
    hasSite ? DD.mobileHeader : cached?.mobileHeader,
  );
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? isMobileHeaderView() : false,
  );

  useEffect(() => {
    const sync = () => setIsMobile(isMobileHeaderView());
    sync();
    window.addEventListener("resize", sync);
    const unsubResume = subscribeTabResume(() => {
      sync();
    });
    return () => {
      window.removeEventListener("resize", sync);
      unsubResume();
    };
  }, []);

  useEffect(() => {
    if (!hasSite) return;
    writeHeaderCache(headerId);
  }, [hasSite, headerId]);

  const path = location.pathname.toLowerCase();
  const showHeader =
    !DD?.isMaintenance &&
    path !== "/maintenance" &&
    ((path !== "/login" && path !== "/change/password") || authToken);

  if (!showHeader) return null;

  const useAlternateHeader = isMobile && headerId === "header2";
  return useAlternateHeader ? (
    <Suspense fallback={null}>
      <Header2 />
    </Suspense>
  ) : (
    <Header />
  );
};

const IdleLogoutWatcher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const cookies = new Cookies();
  const authToken = cookies.get("skyTokenFront");
  const isLogin = useSelector((e: any) => e.isAuthenticated?.isLogin);

  const handleIdle = useCallback(() => {
    markIdleLogoutHome();
    Logout();
    dispatch({
      type: "AUTHENTICATION",
      payload: { isLogin: false, token: "", changePassword: false },
    });
    dispatch({ type: "LOGOUT" });
    dispatch({ type: "SET_BET_HISTORY_SHOW", payload: false });
    document.body.classList.remove("no_scroll");
    navigate("/", { replace: true });
  }, [dispatch, navigate]);

  useIdleLogout({
    enabled: !!(isLogin && authToken),
    onIdle: handleIdle,
  });

  useEffect(() => {
    const onExpired = () => handleIdle();
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [handleIdle]);

  return null;
};

const AccountAwareOverlayLoader = ({ show }: { show: boolean }) => {
  const location = useLocation();
  const mobile = useIsMobileView();
  if (!show) return null;
  if (mobile && isAccountPath(location.pathname)) return null;
  return (
    <div className="loader_top loader_overlay">
      <div className="">
        <Loader />
      </div>
    </div>
  );
};

function App() {
  const dispatch = useDispatch();
  const cookies = new Cookies();
  const authToken = cookies.get("skyTokenFront");

  const betHistoryShow = useSelector((e: any) => e.betHistoryShow);
  const loaderFromStore = useSelector((e: any) => e.loader);
  const [isLoader, setLoader] = useState<any>(false);
  const DD = useSelector((e: any) => e.domainDetails);

  useEffect(() => {
    applySiteFavicon(DD);
  }, [DD]);

  useEffect(() => {
    console.log("isLoader :: ", isLoader);

    setLoader(loaderFromStore);
  }, [loaderFromStore]);

  // DevTools detection — blocks all open methods, bypasses local/dev environments
  useEffect(() => {
    const hostname = window.location.hostname;
    const isExempt =
      hostname === "localhost" ||
      hostname === "mysky247.xyz" ||
      hostname === "jitbo.site";
    if (isExempt) return;

    let devToolsOpen = false;
    const interval = setInterval(() => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      const start = performance.now();
      // Use Function constructor so build tools cannot strip the debugger statement
      // eslint-disable-next-line no-new-func
      new Function("debugger")();
      const elapsed = performance.now() - start;
      if (elapsed > 100 && !devToolsOpen) {
        devToolsOpen = true;
        window.location.replace("https://www.google.com");
      } else if (elapsed <= 100) {
        devToolsOpen = false;
      }
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    syncMobileFooterViewportClass();
    const onResize = () => syncMobileFooterViewportClass();
    window.addEventListener("resize", onResize);
    const unsubResume = subscribeTabResume(() => {
      syncMobileFooterViewportClass();
    });
    return () => {
      window.removeEventListener("resize", onResize);
      unsubResume();
    };
  }, []);

  useEffect(() => {
    if (betHistoryShow) {
      document.body.classList.add("no_scroll");
    } else {
      document.body.classList.remove("no_scroll");
    }
    return () => {
      document.body.classList.remove("no_scroll");
    };
  }, [betHistoryShow]);

  useEffect(() => {
    // getPageData();
    if (!authToken) {
      dispatch({ type: "AUTHENTICATION", payload: false });
    }

    return () => {};
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const theme = getTheme(DD?.colorSchema);
    if (theme) {
      const { headerBgFirst, headerBgSecond, headerTextColor } = theme;
      document.documentElement.style.setProperty(
        "--headerBgColor",
        `linear-gradient(-180deg, ${headerBgFirst} 0%, ${headerBgSecond} 100%)`,
      );
    }
    const matchListTextColor = String(DD?.matchListTextColor || "")
      .trim()
      .replace(/;+\s*$/, "");
    if (matchListTextColor) {
      document.documentElement.style.setProperty(
        "--match-list-text-color",
        matchListTextColor,
      );
    } else {
      document.documentElement.style.removeProperty("--match-list-text-color");
    }
  }, [DD]);
  const getPageData = async () => {
    let data = {
      api: `${USER_API.HOME}?domain=${window.location.hostname}`,
      value: {
        domain:
          window.location.hostname === "localhost"
            ? process.env.REACT_APP_DOMAIN
            : window.location.hostname,
      },
    };

    await getApi(data)
      .then(function (response) {
        console.log("getPageData", response);
        // setPageData(response.data.data)
        dispatch({ type: "SET_HOME_DATA", payload: response.data.data });
        // setLoadingImages(response.data.data.dashboardImagesInfo.map((item: any) => item.id));
      })
      .catch((err) => {
        if (err.response.data.statusCode === 401) {
          // Logout()
          // navigate('/login')
        }
      });
  };

  return (
    <>
      <Helmet>
        <title>{DD?.title || "Asian No.1 Betting Website"}</title>
        <meta
          property="og:title"
          content={DD?.title || "Asian No.1 Betting Website"}
        />
        <meta property="og:description" content="Asian No.1 Betting Website" />
        {DD?.favicon && (
          <meta property="og:image" content={getImageUrl(DD.favicon)} />
        )}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={DD?.title || "Asian No.1 Betting Website"}
        />
      </Helmet>
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="loader_top loader_overlay">
              <div className="">{/* <Loader /> */}</div>
            </div>
          }
        >
          <AccountAwareOverlayLoader show={!!isLoader} />

          {/* {isAuthenticated?.isLogin && authToken ? <Header /> : <></>} */}
          <IdleLogoutWatcher />
          <HeaderWrapper />
          <ToastMessage />
          <ZoomablePageContent>
            <Router />
          </ZoomablePageContent>
        </Suspense>
      </BrowserRouter>
    </>
  );
}

export default App;

/**
 * this code snippet going to use for disable site when user open console
 var $el = function (sel: any) {
        return document.querySelector(sel)
    };
    var isMac = navigator.platform.toLowerCase().indexOf('mac') > -1,
        openedRatio = isMac ? 0.85 : 1.2, // for console.error
        openedRatio = isMac ? 1.6 : 1.5, //for console.log 
        startedOpenedRatio = isMac ? 0.5 : 0.8,
        firstTest: any,
        inter: string | number | NodeJS.Timeout | undefined;

    if (window.location.hostname !== 'localhost') {
        window.addEventListener('load', function () {
            setTimeout(init, 1000);
        })
    }

    function init() {
        firstTest = testDevTools();
        // $el('.ConsoleOpen')!.innerHTML = firstTest;
        $el('.manual')!.addEventListener('click', function (e: { preventDefault: () => void; }) {
            e.preventDefault();
            stopCheck();
            // $el('.ConsoleOpen').innerHTML = testDevTools();
        })
        startCheck();
    }

    function testDevTools() {
        var t = performance.now();
        for (var i = 0; i < 100; i++) {
            // console.log('1');
            console.error('1');
            // console.clear();
        }
        return performance.now() - t;
    }

    function startCheck() {
        stopCheck();
        inter = setInterval(function () {
            var test = testDevTools(),
                ratio = test / firstTest,
                opened = ratio > openedRatio;
            // $el('.ConsoleOpen')!.innerHTML = 'Chrome Console is - ' + (opened ? 'Opened' : 'Closed');
            $el('.ConsoleOpen')!.style.display = opened ? 'block' : 'none';
            if (ratio < startedOpenedRatio) { firstTest = test; }
        }, 1000);
    }

    function stopCheck() {
        clearInterval(inter);
    }
 */
